import { z } from 'zod';
import {
  IdSchema,
  NonEmptyStringSchema,
  OptionalNullableStringSchema,
  TimestampSchema,
} from './common';

export const ExperimentScopeEnum = z.enum(['funnel', 'step']);
export type ExperimentScope = z.infer<typeof ExperimentScopeEnum>;

export const ExperimentStatusEnum = z.enum([
  'draft',
  'running',
  'paused',
  'completed',
]);
export type ExperimentStatus = z.infer<typeof ExperimentStatusEnum>;

export const ExperimentVariantSchema = z.object({
  id: IdSchema.optional(),
  variantKey: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_-]+$/, {
      message: 'variantKey must be lowercase alphanumeric, dash, or underscore',
    }),
  name: z.string().min(1),
  trafficSplit: z.number().int().min(0).max(100),
  isControl: z.boolean().default(false),
  funnelId: OptionalNullableStringSchema,
  stepId: OptionalNullableStringSchema,
});
export type ExperimentVariant = z.infer<typeof ExperimentVariantSchema>;

function variantsSplitToHundred(
  variants: readonly { trafficSplit: number }[],
): boolean {
  const total = variants.reduce((sum, v) => sum + v.trafficSplit, 0);
  return total === 100;
}

function variantKeysUnique(
  variants: readonly { variantKey: string }[],
): boolean {
  return new Set(variants.map((v) => v.variantKey)).size === variants.length;
}

export const ExperimentSchema = z
  .object({
    id: IdSchema,
    tenantId: IdSchema,
    storeId: OptionalNullableStringSchema,
    funnelId: IdSchema,
    stepId: OptionalNullableStringSchema,
    name: z.string().min(1),
    scope: ExperimentScopeEnum,
    status: ExperimentStatusEnum,
    variants: z.array(ExperimentVariantSchema).min(2),
    startDate: TimestampSchema.nullable().optional(),
    endDate: TimestampSchema.nullable().optional(),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
    deletedAt: TimestampSchema.nullable().optional(),
  })
  .refine((e) => variantsSplitToHundred(e.variants), {
    message: 'variant trafficSplit values must sum to 100',
    path: ['variants'],
  })
  .refine((e) => variantKeysUnique(e.variants), {
    message: 'variantKey values must be unique within an experiment',
    path: ['variants'],
  })
  .refine((e) => e.scope !== 'step' || (e.stepId ?? '') !== '', {
    message: 'step-scoped experiments require a stepId',
    path: ['stepId'],
  });
export type Experiment = z.infer<typeof ExperimentSchema>;

export const CreateExperimentRequestSchema = z
  .object({
    name: z.string().min(1),
    funnelId: NonEmptyStringSchema,
    scope: ExperimentScopeEnum.default('funnel'),
    stepId: z.string().optional(),
    status: ExperimentStatusEnum.optional(),
    storeId: z.string().optional(),
    variants: z
      .array(
        ExperimentVariantSchema.pick({
          variantKey: true,
          name: true,
          trafficSplit: true,
          isControl: true,
          funnelId: true,
          stepId: true,
        }),
      )
      .min(2),
    startDate: TimestampSchema.optional(),
    endDate: TimestampSchema.optional(),
  })
  .refine((e) => variantsSplitToHundred(e.variants), {
    message: 'variant trafficSplit values must sum to 100',
    path: ['variants'],
  })
  .refine((e) => variantKeysUnique(e.variants), {
    message: 'variantKey values must be unique within an experiment',
    path: ['variants'],
  })
  .refine((e) => e.scope !== 'step' || (e.stepId ?? '') !== '', {
    message: 'step-scoped experiments require a stepId',
    path: ['stepId'],
  });
export type CreateExperimentRequest = z.infer<
  typeof CreateExperimentRequestSchema
>;

export const UpdateExperimentRequestSchema = z
  .object({
    name: z.string().min(1).optional(),
    status: ExperimentStatusEnum.optional(),
    variants: z
      .array(
        ExperimentVariantSchema.pick({
          id: true,
          variantKey: true,
          name: true,
          trafficSplit: true,
          isControl: true,
          funnelId: true,
          stepId: true,
        }),
      )
      .min(2)
      .optional(),
    startDate: TimestampSchema.nullable().optional(),
    endDate: TimestampSchema.nullable().optional(),
  })
  .refine((e) => e.variants === undefined || variantsSplitToHundred(e.variants), {
    message: 'variant trafficSplit values must sum to 100',
    path: ['variants'],
  })
  .refine((e) => e.variants === undefined || variantKeysUnique(e.variants), {
    message: 'variantKey values must be unique within an experiment',
    path: ['variants'],
  });
export type UpdateExperimentRequest = z.infer<
  typeof UpdateExperimentRequestSchema
>;

export const ExperimentAssignmentSchema = z.object({
  experimentId: IdSchema,
  variantKey: z.string().min(1),
  sessionId: z.string().min(1),
});
export type ExperimentAssignment = z.infer<typeof ExperimentAssignmentSchema>;

export const ExperimentVariantMetricsSchema = z.object({
  variantKey: z.string().min(1),
  name: z.string(),
  isControl: z.boolean().default(false),
  impressions: z.number().int().nonnegative(),
  conversions: z.number().int().nonnegative(),
  conversionRate: z.number().nonnegative(),
  revenue: z.number().nonnegative(),
});
export type ExperimentVariantMetrics = z.infer<
  typeof ExperimentVariantMetricsSchema
>;

export const ExperimentResultsSchema = z.object({
  experimentId: IdSchema,
  status: ExperimentStatusEnum,
  scope: ExperimentScopeEnum,
  variants: z.array(ExperimentVariantMetricsSchema),
});
export type ExperimentResults = z.infer<typeof ExperimentResultsSchema>;

export const ExperimentResponseSchema = z.object({
  experiment: ExperimentSchema,
});
export const ExperimentListResponseSchema = z.object({
  experiments: z.array(ExperimentSchema),
});
export const ExperimentResultsResponseSchema = z.object({
  results: ExperimentResultsSchema,
});
